// src/shared/services/rolePages.js
//
// CHA-553 (A+B) — read a tenant role's page-grant list for nav filtering.
//
// Operator decision (CHA-553 audit): grants come from a Firestore read on
// sign-in, NOT from the custom claim. Reasons on the ticket — grants can
// exceed the claim size budget, they shift without a token refresh, and a
// read is fresh. This is the read side of that decision.
//
// Fails OPEN to `undefined`: a role with no document (`user`/`viewer`) or any
// read error returns undefined, which visibleNavGroups treats as
// allow-by-default (full nav). This is a UX filter, not a security boundary —
// route and callable enforcement are CHA-553 C/D — so failing open is correct:
// a transient read error must never lock a user out of their own app.

import { doc, getDoc } from 'firebase/firestore';
import { FirebaseService } from './FirebaseService';

/**
 * @param {string} tenantId
 * @param {string} roleId
 * @returns {Promise<string[]|undefined>} the role's `pages` array, or undefined
 *   when the doc is missing, has no pages field, or the read fails.
 */
export async function fetchRolePages(tenantId, roleId) {
  if (!tenantId || !roleId) return undefined;
  try {
    const db = FirebaseService.getFirestore();
    const snap = await getDoc(doc(db, 'tenants', tenantId, 'roles', roleId));
    if (!snap.exists()) return undefined;
    const pages = snap.data()?.pages;
    // Fail open on a malformed field: only a real string[] is trustworthy.
    // `['data/powerbi', null]` must NOT reach visibleNavGroups as a grant
    // list — validate every entry, else undefined (allow-by-default).
    // An explicit empty [] IS valid and meaningful (zero-access role), so it
    // passes through; visibleNavGroups renders it as an empty nav.
    if (!Array.isArray(pages) || !pages.every((p) => typeof p === 'string')) {
      return undefined;
    }
    return pages;
  } catch (err) {
    console.warn('CHA-553: role.pages read failed, allowing full nav:', err?.message || err);
    return undefined;
  }
}
